import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const quickLinks = [
  { label: 'Anasayfa', href: '/' },
  { label: 'Servis Al', href: '/servis' },
  { label: 'Cihaz Sorgulama', href: '/cihaz-sorgulama' },
  { label: 'Hakkında', href: '/hakkinda' },
  { label: 'Blog', href: '/blog' },
  { label: 'Destek', href: '/destek' },
  { label: 'İletişim', href: '/iletisim' },
];

const serviceLinks = [
  { label: 'Apple Servisi', href: '/blog/apple' },
  { label: 'Samsung Servisi', href: '/blog/samsung' },
  { label: 'Xiaomi/Redmi Servisi', href: '/blog/xiaomi' },
  { label: 'Ekran Değişimi', href: '/servis' },
  { label: 'Batarya Değişimi', href: '/servis' },
  { label: 'Anakart Tamiri', href: '/servis' },
];

// Custom Icons for TikTok and X
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const InstagramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const socialLinks = [
  { icon: InstagramIcon, href: 'https://www.instagram.com/teslaprotr/', label: 'Instagram' },
  { icon: TikTokIcon, href: 'https://www.tiktok.com/@teslaprotr', label: 'TikTok' },
  { icon: XIcon, href: 'https://x.com/Teslaprotr', label: 'X' },
];

// Feedback Banner Component
export const FeedbackBanner = () => {
  return (
    <section className="bg-muted py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Feedback Form */}
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold font-display text-foreground mb-2">
                Şikayet ve Görüşleriniz
              </h2>
              <p className="text-muted-foreground">
                Hizmetlerimizi geliştirmemiz için görüşleriniz bizim için çok değerli.
              </p>
            </div>
            <form className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input placeholder="Adınız Soyadınız" />
                <Input placeholder="E-posta Adresiniz" type="email" />
              </div>
              <Input placeholder="Konu" />
              <Textarea placeholder="Mesajınız..." rows={4} />
              <Button type="submit" className="w-full sm:w-auto">
                Gönder
              </Button>
            </form>
          </div>

          {/* Divider */}
          <div className="hidden lg:flex items-center justify-center">
            <div className="w-px h-80 bg-border" />
          </div>

          {/* Right Side - Contact Info */}
          <div className="space-y-6 lg:pl-12">
            <h2 className="text-2xl sm:text-3xl font-bold font-display text-foreground">
              İletişim Bilgilerimiz
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Adres</p>
                  <p className="text-muted-foreground text-sm">
                    Alaybey, 1690 ARABACILAR Sok. No:130/A<br />
                    35560 Karşıyaka/İzmir
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Telefon</p>
                  <p className="text-muted-foreground text-sm">+90 (232) 364 69 83</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground">E-posta</p>
                  <p className="text-muted-foreground text-sm">teslaprotr@gmail.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Çalışma Saatleri</p>
                  <p className="text-muted-foreground text-sm">
                    Pzt - Cmt: 10:30 - 19:30<br />
                    Pazar: Kapalı
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-foreground text-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand Section */}
          <div className="space-y-4">
            <div>
              <h3 className="text-xl font-bold font-display tracking-wider">
                TESLA PRO
              </h3>
              <p className="text-xs tracking-[0.15em] text-background/60 uppercase">
                Profesyonel Teknik Servis
              </p>
            </div>
            <p className="text-sm text-background/70 leading-relaxed">
              Türkiye'nin güvenilir teknik servis noktası. Profesyonel ekibimizle 
              tüm marka cihazlarınız için kaliteli hizmet sunuyoruz.
            </p>
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="w-9 h-9 rounded-full bg-background/10 flex items-center justify-center hover:bg-primary transition-colors"
                  aria-label={social.label}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">
              Hızlı Bağlantılar
            </h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-background/70 hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">
              Servislerimiz
            </h4>
            <ul className="space-y-2">
              {serviceLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-background/70 hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">
              İletişim
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-sm text-background/70">
                  Alaybey, 1690 ARABACILAR Sok. No:130/A<br />
                  35560 Karşıyaka/İzmir
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                <a
                  href="tel:+902323646983"
                  className="text-sm text-background/70 hover:text-primary transition-colors"
                >
                  +90 (232) 364 69 83
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                <a
                  href="mailto:teslaprotr@gmail.com"
                  className="text-sm text-background/70 hover:text-primary transition-colors"
                >
                  teslaprotr@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-sm text-background/70">
                  Pzt - Cmt: 10:30 - 19:30
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-background/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-background/60">
              © 2024 Teslapro Teknik Servis. Tüm hakları saklıdır.
            </p>
            <div className="flex items-center gap-6">
              <Link
                to="/gizlilik"
                className="text-sm text-background/60 hover:text-primary transition-colors"
              >
                Gizlilik Politikası
              </Link>
              <Link
                to="/kullanim-kosullari"
                className="text-sm text-background/60 hover:text-primary transition-colors"
              >
                Kullanım Koşulları
              </Link>
              <Link
                to="/kvkk"
                className="text-sm text-background/60 hover:text-primary transition-colors"
              >
                KVKK
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
